import { Mic, Crosshair, Scissors, Sparkles, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface StepDefinition {
  status: string;
  Icon: LucideIcon;
}

const STEPS: StepDefinition[] = [
  { status: "transcribing", Icon: Mic },
  { status: "segmenting", Icon: Crosshair },
  { status: "trimming", Icon: Scissors },
  { status: "curating", Icon: Sparkles },
];

function getActiveStepIndex(status: string): number {
  const index = STEPS.findIndex((step) => step.status === status);
  return index >= 0 ? index : 0;
}

interface ProcessingStepperProps {
  status: string;
  className?: string;
  variant?: "overlay" | "surface";
}

export const ProcessingStepper = ({
  status,
  className,
  variant = "overlay",
}: ProcessingStepperProps) => {
  const activeIndex = getActiveStepIndex(status);
  const activeStatus = STEPS[activeIndex]?.status ?? STEPS[0].status;
  const isSurface = variant === "surface";
  // The connector leading into the active step animates; if the active
  // step is the first one (nothing precedes it), animate the connector
  // leading out of it instead so movement is always visible.
  const flowingConnectorIndex = activeIndex === 0 ? 1 : activeIndex;

  const items: JSX.Element[] = [];

  STEPS.forEach((step, index) => {
    const isDone = index < activeIndex;
    const isActive = index === activeIndex;
    const Icon = step.Icon;

    if (index > 0) {
      items.push(
        <div
          key={`${step.status}-connector`}
          className={cn(
            "h-0.5 flex-1 min-w-[8px] mx-0.5 rounded-full",
            index <= activeIndex ? "bg-primary/60" : isSurface ? "bg-border" : "bg-white/15",
            index === flowingConnectorIndex && "processing-connector-flow"
          )}
        />
      );
    }

    items.push(
      <div
        key={step.status}
        className={cn(
          "relative flex items-center justify-center rounded-full border shrink-0",
          isSurface ? "w-7 h-7" : "w-[19px] h-[19px]",
          isDone && "bg-primary border-primary",
          isActive && (isSurface ? "border-primary bg-primary/10" : "border-primary bg-black/30"),
          !isDone && !isActive && (isSurface ? "border-border bg-muted" : "border-white/15 bg-black/30")
        )}
      >
        {isActive && (
          <span className="absolute -inset-1.5 rounded-full border border-primary/50 motion-safe:animate-ping" />
        )}
        <Icon
          className={cn(
            isSurface ? "w-3.5 h-3.5" : "w-2.5 h-2.5",
            isDone && "text-primary-foreground",
            isActive && "text-primary",
            !isDone && !isActive && (isSurface ? "text-muted-foreground" : "text-white/40")
          )}
        />
      </div>
    );
  });

  return (
    <div
      className={cn("flex items-center", className)}
      aria-hidden="true"
      data-testid="processing-stepper"
      data-active-status={activeStatus}
    >
      {items}
    </div>
  );
};
